import React, { useEffect, useState } from "react";
import { collection, deleteDoc, doc, onSnapshot, orderBy, query, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { Announcement } from "../types";

// ── Klubin tiedotteet ─────────────────────────────────────────
//
// Ylläpitäjä kirjoittaa asetuksissa, pelaajat näkevät Uutiset-välilehdellä.
// Uusi tiedote laukaisee pushin kaikille klubin laitteille
// (functions: sendAnnouncementPush). Tiedotteet ovat lyhyitä ja niitä on
// vähän, joten koko lista kuunnellaan reaaliaikaisesti.

const C = {
  card: "var(--sl-card)",
  navy: "var(--sl-primary)", gold: "var(--sl-accent)", text: "var(--sl-text)", muted: "var(--sl-muted)", white: "#ffffff",
  border: "var(--sl-border)", soft: "var(--sl-soft)", error: "var(--sl-error)",
};
const F = { heading: "'Rajdhani', sans-serif", body: "'Sora', sans-serif" };
const card: React.CSSProperties = {
  background: C.card, border: `1px solid ${C.border}`,
  borderRadius: 16, padding: 18, marginBottom: 14,
  boxShadow: "0 4px 16px rgba(22,59,104,0.06)",
};
const btn: React.CSSProperties = {
  padding: "10px 18px", borderRadius: 10, border: "none",
  cursor: "pointer", fontWeight: 700, fontSize: 13, fontFamily: F.body,
};

const fmt = (iso: string) =>
  new Date(iso).toLocaleString("fi-FI", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" });

/** Klubin tiedotteet uusin ensin. */
export function useAnnouncements(clubId: string): { items: Announcement[]; loaded: boolean } {
  const [items, setItems] = useState<Announcement[]>([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "clubs", clubId, "announcements"), orderBy("createdAt", "desc")),
      snap => { setItems(snap.docs.map(d => ({ id: d.id, ...d.data() } as Announcement))); setLoaded(true); },
      err => { console.error("announcements", err); setLoaded(true); }
    );
    return () => unsub();
  }, [clubId]);
  return { items, loaded };
}

/** Pelaajan lista. Tyhjänä ei näytä mitään: tiedotteet ovat harvinaisia. */
export const AnnouncementsList: React.FC<{ clubId: string; lang?: "fi" | "en" }> = ({ clubId, lang = "fi" }) => {
  const { items } = useAnnouncements(clubId);
  if (items.length === 0) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
        📣 {lang === "fi" ? "Klubin tiedotteet" : "Club announcements"}
      </div>
      {items.map(a => (
        <div key={a.id} style={{ ...card, padding: "14px 16px", borderLeft: `4px solid ${C.gold}` }}>
          <div style={{ fontSize: 14, color: C.text, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{a.text}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>{fmt(a.createdAt)}</div>
        </div>
      ))}
    </div>
  );
};

/** Ylläpitäjän editori: kirjoita, julkaise, poista. */
export const AnnouncementsAdmin: React.FC<{ clubId: string; lang?: "fi" | "en" }> = ({ clubId, lang = "fi" }) => {
  const { items, loaded } = useAnnouncements(clubId);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function publish() {
    const t = text.trim();
    if (!t) return;
    const ok = window.confirm(lang === "fi"
      ? "Julkaistaanko tiedote? Kaikki klubin pelaajat saavat siitä ilmoituksen."
      : "Publish this announcement? Every club player gets a notification.");
    if (!ok) return;
    setSaving(true);
    setError(null);
    try {
      const ref = doc(collection(db, "clubs", clubId, "announcements"));
      await setDoc(ref, { text: t, createdAt: new Date().toISOString() });
      setText("");
    } catch (err) {
      console.error("announcement", err);
      setError(lang === "fi" ? "Julkaisu epäonnistui. Tarkista yhteys ja yritä uudelleen." : "Could not publish. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(a: Announcement) {
    if (!window.confirm(lang === "fi" ? "Poistetaanko tiedote?" : "Delete this announcement?")) return;
    try { await deleteDoc(doc(db, "clubs", clubId, "announcements", a.id)); } catch (err) { console.error("announcement delete", err); }
  }

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 16 }}>
        📣 {lang === "fi" ? "Tiedotteet" : "Announcements"}
      </div>
      <div style={card}>
        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5, marginBottom: 10 }}>
          {lang === "fi"
            ? "Lyhyt viesti kaikille klubin pelaajille: vuoron siirto, kauden palkinnot, uusi pelipaikka. Näkyy pelaajan Uutiset-välilehdellä, ja jokainen saa siitä ilmoituksen puhelimeen."
            : "A short message to every club player: a moved session, season prizes, a new venue. Shown on the player's News tab, and everyone gets a push notification."}
        </div>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={3} maxLength={500}
          placeholder={lang === "fi" ? "Kirjoita tiedote…" : "Write an announcement…"}
          style={{
            width: "100%", padding: "12px 14px", boxSizing: "border-box", resize: "vertical",
            background: C.card, border: `1.5px solid ${C.border}`, borderRadius: 10,
            fontSize: 14, color: C.text, fontFamily: F.body, outline: "none", marginBottom: 8,
          }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: C.muted }}>{text.length}/500</span>
          <button onClick={publish} disabled={saving || !text.trim()}
            style={{ ...btn, background: C.navy, color: C.white, opacity: (saving || !text.trim()) ? 0.5 : 1 }}>
            {saving ? (lang === "fi" ? "Julkaistaan…" : "Publishing…") : (lang === "fi" ? "📣 Julkaise" : "📣 Publish")}
          </button>
        </div>
        {error && <div style={{ fontSize: 12, color: C.error, marginTop: 8 }}>{error}</div>}

        {loaded && items.length > 0 && (
          <div style={{ marginTop: 14, borderTop: `1px solid ${C.border}` }}>
            {items.map(a => (
              <div key={a.id} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 0", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{a.text}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{fmt(a.createdAt)}</div>
                </div>
                <button onClick={() => remove(a)} style={{ ...btn, background: C.soft, color: C.error, padding: "6px 10px", fontSize: 12 }}>
                  {lang === "fi" ? "Poista" : "Delete"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
